/** Small RFC 4180 CSV reader and the suppression-list importer built on it. */

import type { SuppressionInput, SuppressionKind } from './types'

/** Parse CSV text into rows of cells. Handles quotes, escaped quotes and CRLF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text

  for (let i = 0; i < src.length; i++) {
    const ch = src[i]
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cell += ch
      }
      continue
    }
    if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      row.push(cell)
      cell = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++
      row.push(cell)
      cell = ''
      if (row.some((c) => c.trim() !== '')) rows.push(row)
      row = []
    } else {
      cell += ch
    }
  }
  row.push(cell)
  if (row.some((c) => c.trim() !== '')) rows.push(row)
  return rows.map((r) => r.map((c) => c.trim()))
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i

/** Guess the kind of a bare value: address, domain (optionally with a leading @) or company. */
export function guessKind(value: string): SuppressionKind {
  const v = value.trim()
  if (EMAIL_RE.test(v)) return 'email'
  const stripped = v.replace(/^@+/, '')
  if (DOMAIN_RE.test(stripped)) return 'domain'
  return 'company'
}

export function normalizeSuppressionValue(kind: SuppressionKind, value: string): string {
  let v = value.trim().toLowerCase()
  if (kind === 'domain') v = v.replace(/^@+/, '')
  return v
}

export function isValidSuppression(kind: SuppressionKind, value: string): boolean {
  const v = normalizeSuppressionValue(kind, value)
  if (!v) return false
  if (kind === 'email') return EMAIL_RE.test(v)
  if (kind === 'domain') return DOMAIN_RE.test(v)
  return !v.includes('@')
}

export interface SuppressionCsv {
  rows: SuppressionInput[]
  /** Lines that were skipped as unusable, with the line text. */
  invalid: string[]
  duplicates: number
}

/**
 * Accepts either a single column of values, or a header row with
 * `kind,value[,reason]` / `value,kind,reason` style columns. Kind is guessed
 * from the value when there is no kind column or the cell is empty.
 */
export function parseSuppressionCsv(text: string, source = 'csv import'): SuppressionCsv {
  const table = parseCsv(text)
  const out: SuppressionCsv = { rows: [], invalid: [], duplicates: 0 }
  if (table.length === 0) return out

  const header = (table[0] ?? []).map((c) => c.toLowerCase())
  const valueCol = header.findIndex((h) =>
    ['value', 'email', 'address', 'domain', 'company'].includes(h),
  )
  const hasHeader = valueCol >= 0 || header.includes('kind') || header.includes('type')
  const kindCol = header.findIndex((h) => h === 'kind' || h === 'type')
  const reasonCol = header.findIndex((h) => h === 'reason' || h === 'note' || h === 'notes')
  const vCol = hasHeader ? (valueCol >= 0 ? valueCol : kindCol === 0 ? 1 : 0) : 0
  const body = hasHeader ? table.slice(1) : table

  const seen = new Set<string>()
  for (const cells of body) {
    const rawValue = cells[vCol] ?? ''
    if (!rawValue) continue
    const rawKind = kindCol >= 0 ? (cells[kindCol] ?? '').toLowerCase() : ''
    const kind: SuppressionKind =
      rawKind === 'email' || rawKind === 'domain' || rawKind === 'company'
        ? rawKind
        : guessKind(rawValue)
    if (!isValidSuppression(kind, rawValue)) {
      out.invalid.push(cells.join(','))
      continue
    }
    const value = normalizeSuppressionValue(kind, rawValue)
    const key = `${kind}:${value}`
    if (seen.has(key)) {
      out.duplicates++
      continue
    }
    seen.add(key)
    const reason = reasonCol >= 0 ? (cells[reasonCol] ?? '') : ''
    out.rows.push({ kind, value, source, ...(reason ? { reason } : {}) })
  }
  return out
}
